import { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from "../../lib/supabase";

export interface FocalPoint {
  x: number; // 0-100 percent
  y: number; // 0-100 percent
}

export interface BannerUploaderProps {
  imageUrl: string;
  focalPoint?: FocalPoint;
  onChange: (url: string, focal: FocalPoint) => void;
  aspectRatio?: number; // width/height, default 21/9
  folder?: string;
  label?: string;
  previewLabel?: string;
}

// Convert focal point to CSS object-position
export const focalToCssPosition = (focal: FocalPoint): string =>
  `${focal.x}% ${focal.y}%`;

// Convert image to WebP blob (lossless-quality)
const convertToWebP = (file: File, quality = 0.92): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) { reject(new Error("No canvas context")); return; }
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (blob) resolve(blob);
          else reject(new Error("Conversion failed"));
        },
        "image/webp",
        quality
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Image load failed")); };
    img.src = url;
  });

// ─── Smart Crop Modal ───────────────────────────────────────────────────────
interface CropModalProps {
  src: string;
  aspectRatio: number;
  onConfirm: (croppedBlob: Blob, focal: FocalPoint) => void;
  onCancel: () => void;
}

const CropModal: React.FC<CropModalProps> = ({ src, aspectRatio, onConfirm, onCancel }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Crop box state (in % of image display area)
  const [cropBox, setCropBox] = useState({ x: 10, y: 10, w: 80, h: 80 });
  const [zoom, setZoom] = useState(1);
  const [imgOffset, setImgOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState<"crop" | "img" | null>(null);
  const [dragStart, setDragStart] = useState({ mx: 0, my: 0, ox: 0, oy: 0, cx: 0, cy: 0 });
  const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });
  const [resolutionWarning, setResolutionWarning] = useState("");

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImgNatural({ w: img.naturalWidth, h: img.naturalHeight });
      if (img.naturalWidth < 1920) {
        setResolutionWarning(`Ảnh ${img.naturalWidth}px < 1920px — có thể bị vỡ nét trên màn hình lớn`);
      }
      // Auto-fit crop box to aspect ratio
      const imgAspect = img.naturalWidth / img.naturalHeight;
      if (imgAspect > aspectRatio) {
        // Image wider than crop ratio — fit height
        const cropH = 80;
        const cropW = Math.min(90, cropH * aspectRatio / (imgAspect > 0 ? 1 : 1));
        setCropBox({ x: (100 - cropW) / 2, y: 10, w: cropW, h: cropH });
      } else {
        const cropW = 80;
        const cropH = Math.min(90, cropW / aspectRatio);
        setCropBox({ x: 10, y: (100 - cropH) / 2, w: cropW, h: cropH });
      }
    };
    img.src = src;
  }, [src, aspectRatio]);

  const handleImgMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setDragging("img");
    setDragStart({ mx: e.clientX, my: e.clientY, ox: imgOffset.x, oy: imgOffset.y, cx: 0, cy: 0 });
  };

  const handleCropMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging("crop");
    setDragStart({ mx: e.clientX, my: e.clientY, ox: 0, oy: 0, cx: cropBox.x, cy: cropBox.y });
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const dx = ((e.clientX - dragStart.mx) / rect.width) * 100;
      const dy = ((e.clientY - dragStart.my) / rect.height) * 100;
      if (dragging === "img") {
        setImgOffset({ x: dragStart.ox + dx, y: dragStart.oy + dy });
      } else if (dragging === "crop") {
        const newX = Math.max(0, Math.min(100 - cropBox.w, dragStart.cx + dx));
        const newY = Math.max(0, Math.min(100 - cropBox.h, dragStart.cy + dy));
        setCropBox((prev) => ({ ...prev, x: newX, y: newY }));
      }
    };
    const onUp = () => setDragging(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [dragging, dragStart, cropBox.w, cropBox.h]);

  const handleConfirm = () => {
    if (!imgRef.current || !containerRef.current) return;
    const container = containerRef.current.getBoundingClientRect();
    const imgEl = imgRef.current;
    const imgRect = imgEl.getBoundingClientRect();

    // Calculate focal point as center of crop box relative to image
    const cropCenterXPx = (cropBox.x / 100) * container.width + (cropBox.w / 100) * container.width / 2;
    const cropCenterYPx = (cropBox.y / 100) * container.height + (cropBox.h / 100) * container.height / 2;
    const focalX = Math.max(0, Math.min(100, ((cropCenterXPx - (imgRect.left - container.left)) / imgRect.width) * 100));
    const focalY = Math.max(0, Math.min(100, ((cropCenterYPx - (imgRect.top - container.top)) / imgRect.height) * 100));

    // Draw cropped canvas
    const canvas = canvasRef.current!;
    const cropXRatio = cropBox.x / 100;
    const cropYRatio = cropBox.y / 100;
    const cropWRatio = cropBox.w / 100;
    const cropHRatio = cropBox.h / 100;

    // Map crop box (in container %) to natural image pixels
    const scaleX = imgNatural.w / imgRect.width;
    const scaleY = imgNatural.h / imgRect.height;
    const imgOffsetXPx = imgRect.left - container.left;
    const imgOffsetYPx = imgRect.top - container.top;

    const cropPxX = (cropXRatio * container.width - imgOffsetXPx) * scaleX;
    const cropPxY = (cropYRatio * container.height - imgOffsetYPx) * scaleY;
    const cropPxW = cropWRatio * container.width * scaleX;
    const cropPxH = cropHRatio * container.height * scaleY;

    // Output canvas at target aspect ratio, max 1920px wide
    const outW = Math.min(1920, Math.max(800, Math.round(cropPxW)));
    const outH = Math.round(outW / aspectRatio);
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d")!;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      ctx.drawImage(img, Math.max(0, cropPxX), Math.max(0, cropPxY), cropPxW, cropPxH, 0, 0, outW, outH);
      canvas.toBlob((blob) => {
        if (blob) onConfirm(blob, { x: Math.round(focalX), y: Math.round(focalY) });
      }, "image/webp", 0.92);
    };
    img.src = src;
  };

  const cropAspectH = (cropBox.w / aspectRatio);

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
      <canvas ref={canvasRef} className="hidden" />
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="font-bold text-gray-800 text-sm">Smart Crop & Zoom</h3>
            <p className="text-xs text-gray-400 mt-0.5">Kéo khung vàng để chọn vùng hiển thị · Kéo ảnh để di chuyển · Zoom để phóng to/thu nhỏ</p>
          </div>
          <button onClick={onCancel} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        {/* Resolution warning */}
        {resolutionWarning && (
          <div className="mx-5 mt-3 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex-shrink-0">
            <i className="ri-alert-line text-amber-500 flex-shrink-0" />
            <p className="text-xs text-amber-700">{resolutionWarning}</p>
          </div>
        )}

        {/* Crop area */}
        <div className="flex-1 overflow-hidden p-5">
          <div
            ref={containerRef}
            className="relative w-full overflow-hidden rounded-xl bg-gray-900 select-none"
            style={{ height: 380, cursor: dragging === "img" ? "grabbing" : "grab" }}
            onMouseDown={handleImgMouseDown}
          >
            {/* Image */}
            <img
              ref={imgRef}
              src={src}
              alt="Crop preview"
              className="absolute pointer-events-none"
              style={{
                transform: `scale(${zoom}) translate(${imgOffset.x / zoom}%, ${imgOffset.y / zoom}%)`,
                transformOrigin: "center center",
                width: "100%",
                height: "100%",
                objectFit: "contain",
                imageRendering: "high-quality" as React.CSSProperties["imageRendering"],
              }}
              draggable={false}
            />

            {/* Dark overlay outside crop box */}
            <div className="absolute inset-0 pointer-events-none" style={{ background: "rgba(0,0,0,0.5)" }}>
              {/* Cut out the crop box using clip-path trick via box-shadow */}
            </div>

            {/* Crop box */}
            <div
              className="absolute border-2 border-yellow-400 cursor-move z-10"
              style={{
                left: `${cropBox.x}%`,
                top: `${cropBox.y}%`,
                width: `${cropBox.w}%`,
                height: `${cropAspectH}%`,
                boxShadow: "0 0 0 9999px rgba(0,0,0,0.5)",
              }}
              onMouseDown={handleCropMouseDown}
            >
              {/* Rule of thirds grid */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/20" />
                <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/20" />
                <div className="absolute top-1/3 left-0 right-0 h-px bg-white/20" />
                <div className="absolute top-2/3 left-0 right-0 h-px bg-white/20" />
              </div>
              {/* Corner handles */}
              {[["top-0 left-0", "-translate-x-1/2 -translate-y-1/2"], ["top-0 right-0", "translate-x-1/2 -translate-y-1/2"], ["bottom-0 left-0", "-translate-x-1/2 translate-y-1/2"], ["bottom-0 right-0", "translate-x-1/2 translate-y-1/2"]].map(([pos, tr], i) => (
                <div key={i} className={`absolute ${pos} w-3 h-3 bg-yellow-400 rounded-sm transform ${tr}`} />
              ))}
              {/* Safe zone */}
              <div className="absolute inset-[15%] border border-dashed border-white/40 rounded pointer-events-none flex items-center justify-center">
                <span className="text-white/50 text-[9px] font-bold bg-black/30 px-1.5 py-0.5 rounded whitespace-nowrap">VÙNG AN TOÀN</span>
              </div>
              {/* Drag hint */}
              <div className="absolute top-1 right-1 bg-yellow-400/90 text-black text-[8px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap">
                KÉO ĐỂ DI CHUYỂN
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="px-5 pb-4 flex-shrink-0 space-y-3">
          <div className="flex items-center gap-3">
            <i className="ri-zoom-out-line text-gray-500 text-sm flex-shrink-0" />
            <input
              type="range"
              min={0.5}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1 accent-[#004A99]"
            />
            <i className="ri-zoom-in-line text-gray-500 text-sm flex-shrink-0" />
            <span className="text-xs font-bold text-[#004A99] w-10 text-right">{zoom.toFixed(1)}x</span>
            <button
              onClick={() => { setZoom(1); setImgOffset({ x: 0, y: 0 }); }}
              className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap px-2 py-1 rounded hover:bg-gray-100"
            >
              Reset
            </button>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <i className="ri-information-line text-gray-400" />
            <span>Tỷ lệ khóa: <strong className="text-gray-700">{aspectRatio >= 2 ? "21:9 (Banner rộng)" : aspectRatio >= 1.5 ? "16:9" : aspectRatio >= 1 ? "4:3" : "1:1 (Vuông)"}</strong> · Ảnh đầu ra: <strong className="text-gray-700">WebP chất lượng cao</strong></span>
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer whitespace-nowrap">
              Hủy
            </button>
            <button
              onClick={handleConfirm}
              className="flex items-center gap-2 px-5 py-2 bg-[#004A99] text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap hover:bg-[#003a7a] transition-colors"
            >
              <i className="ri-crop-line" />
              Xác nhận & Cắt ảnh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main BannerUploader ────────────────────────────────────────────────────
const BannerUploader: React.FC<BannerUploaderProps> = ({
  imageUrl,
  focalPoint = { x: 50, y: 50 },
  onChange,
  aspectRatio = 21 / 9,
  folder = "banners",
  label = "Ảnh Banner",
  previewLabel = "Xem trước",
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [focal, setFocal] = useState<FocalPoint>(focalPoint);
  const [isDraggingFocal, setIsDraggingFocal] = useState(false);
  const [resolutionWarning, setResolutionWarning] = useState("");
  const [localPreview, setLocalPreview] = useState<string>("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSafeZone, setShowSafeZone] = useState(true);
  const [showCropModal, setShowCropModal] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFocal(focalPoint);
  }, [focalPoint.x, focalPoint.y]);

  const checkResolution = (img: HTMLImageElement) => {
    if (img.naturalWidth < 1920) {
      setResolutionWarning(`Ảnh có thể bị vỡ nét! Chiều ngang ${img.naturalWidth}px < 1920px khuyến nghị.`);
    } else {
      setResolutionWarning("");
    }
  };

  const uploadBlob = async (blob: Blob): Promise<string | null> => {
    setUploadProgress(20);
    const fileName = `${folder}/${Date.now()}_${Math.random().toString(36).slice(2)}.webp`;
    const { data, error } = await supabase.storage
      .from("bizwell-images")
      .upload(fileName, blob, { contentType: "image/webp", upsert: false });
    setUploadProgress(80);
    if (error || !data) return null;
    const { data: urlData } = supabase.storage
      .from("bizwell-images")
      .getPublicUrl(data.path);
    setUploadProgress(100);
    return urlData.publicUrl + `?t=${Date.now()}`;
  };

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const file = files[0];
      const localUrl = URL.createObjectURL(file);
      // Check resolution
      const img = new Image();
      img.onload = () => { checkResolution(img); };
      img.src = localUrl;
      // Show crop modal
      setPendingFile(file);
      setPendingPreview(localUrl);
      setShowCropModal(true);
    },
    [folder]
  );

  const handleCropConfirm = async (croppedBlob: Blob, newFocal: FocalPoint) => {
    setShowCropModal(false);
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingFile(null);
    setPendingPreview("");
    setUploading(true);
    setUploadProgress(0);
    setLocalPreview(URL.createObjectURL(croppedBlob));
    const url = await uploadBlob(croppedBlob);
    setUploading(false);
    setUploadProgress(0);
    if (url) {
      setLocalPreview("");
      setFocal(newFocal);
      onChange(url, newFocal);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingFile(null);
    setPendingPreview("");
  };

  // Focal point drag on preview
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingFocal || !previewRef.current) return;
      const rect = previewRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
      const newFocal = { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
      setFocal(newFocal);
      onChange(imageUrl, newFocal);
    };
    const handleMouseUp = () => setIsDraggingFocal(false);
    if (isDraggingFocal) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDraggingFocal, imageUrl, onChange]);

  const displayUrl = localPreview || imageUrl;
  const paddingTop = `${(1 / aspectRatio) * 100}%`;

  return (
    <>
      {showCropModal && pendingPreview && (
        <CropModal
          src={pendingPreview}
          aspectRatio={aspectRatio}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="block text-xs font-semibold text-gray-600">{label}</label>
          <div className="flex items-center gap-2">
            {displayUrl && (
              <>
                <button
                  type="button"
                  onClick={() => setShowSafeZone((v) => !v)}
                  className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-full font-semibold transition-colors cursor-pointer ${
                    showSafeZone ? "bg-[#004A99]/10 text-[#004A99]" : "bg-gray-100 text-gray-500"
                  }`}
                >
                  <i className="ri-shield-check-line" />
                  {showSafeZone ? "Ẩn vùng an toàn" : "Hiện vùng an toàn"}
                </button>
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-crop-line" />
                  Cắt ảnh
                </button>
              </>
            )}
            <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
              WebP · Smart Crop
            </span>
          </div>
        </div>

        {/* Resolution warning */}
        {resolutionWarning && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <i className="ri-alert-line text-amber-500 flex-shrink-0" />
            <p className="text-xs text-amber-700">{resolutionWarning}</p>
          </div>
        )}

        {/* Preview area with focal point */}
        {displayUrl ? (
          <div className="space-y-2">
            {/* Main preview with focal point drag */}
            <div className="relative rounded-xl overflow-hidden bg-gray-900" style={{ paddingTop }}>
              <div
                ref={previewRef}
                className="absolute inset-0"
                style={{ cursor: isDraggingFocal ? "crosshair" : "default" }}
                onClick={(e) => {
                  if (!previewRef.current) return;
                  const rect = previewRef.current.getBoundingClientRect();
                  const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
                  const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
                  const newFocal = { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
                  setFocal(newFocal);
                  onChange(imageUrl, newFocal);
                }}
              >
                <img
                  src={displayUrl}
                  alt="Banner preview"
                  className="w-full h-full"
                  style={{
                    objectFit: "cover",
                    objectPosition: focalToCssPosition(focal),
                    imageRendering: "high-quality" as React.CSSProperties["imageRendering"],
                  }}
                  draggable={false}
                />

                {/* Safe zone overlay */}
                {showSafeZone && (
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 border-[20px] border-black/20" />
                    <div className="absolute inset-[15%] border-2 border-dashed border-white/50 rounded-lg">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-white/60 text-[10px] font-bold bg-black/30 px-2 py-1 rounded whitespace-nowrap">
                          VÙNG AN TOÀN — Text sẽ hiển thị ở đây
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Focal point indicator */}
                <div
                  className="absolute z-20 pointer-events-none"
                  style={{ left: `${focal.x}%`, top: `${focal.y}%`, transform: "translate(-50%, -50%)" }}
                >
                  <div className="relative">
                    <div className="w-6 h-6 rounded-full border-2 border-white bg-[#004A99]/60 shadow-lg" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-white" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-px w-8 h-px bg-white/60" style={{ marginLeft: -16 }} />
                    <div className="absolute top-1/2 left-1/2 -translate-x-px -translate-y-1/2 w-px h-8 bg-white/60" style={{ marginTop: -16 }} />
                  </div>
                </div>

                {/* Draggable focal handle */}
                <div
                  className="absolute z-30"
                  style={{
                    left: `${focal.x}%`,
                    top: `${focal.y}%`,
                    transform: "translate(-50%, -50%)",
                    cursor: isDraggingFocal ? "crosshair" : "grab",
                    width: 32,
                    height: 32,
                  }}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingFocal(true); }}
                />

                {/* Upload overlay */}
                {uploading && (
                  <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-3">
                    <i className="ri-loader-4-line text-white text-3xl animate-spin" />
                    <div className="w-32 h-1.5 bg-white/20 rounded-full overflow-hidden">
                      <div className="h-full bg-white rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                    </div>
                    <p className="text-white text-xs font-semibold">
                      {uploadProgress < 40 ? "Đang xử lý..." : uploadProgress < 80 ? "Đang tải lên..." : "Hoàn tất..."}
                    </p>
                  </div>
                )}

                {/* Top-right controls */}
                <div className="absolute top-2 right-2 flex gap-1.5 z-40">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                    className="flex items-center gap-1 bg-white/90 hover:bg-white text-gray-700 text-[10px] font-semibold px-2 py-1 rounded-lg shadow cursor-pointer whitespace-nowrap transition-colors"
                  >
                    <i className="ri-refresh-line text-xs" />
                    Đổi ảnh
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onChange("", { x: 50, y: 50 }); setLocalPreview(""); setResolutionWarning(""); }}
                    className="flex items-center gap-1 bg-red-500/90 hover:bg-red-500 text-white text-[10px] font-semibold px-2 py-1 rounded-lg shadow cursor-pointer whitespace-nowrap transition-colors"
                  >
                    <i className="ri-delete-bin-line text-xs" />
                    Xóa
                  </button>
                </div>
              </div>
            </div>

            {/* Focal point info + controls */}
            <div className="flex items-center gap-3 bg-[#F8F9FA] rounded-lg px-3 py-2">
              <div className="w-5 h-5 flex items-center justify-center bg-[#004A99]/10 rounded flex-shrink-0">
                <i className="ri-focus-3-line text-[#004A99] text-xs" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-semibold text-gray-600">
                  Điểm tập trung (Focal Point): <span className="text-[#004A99]">{focal.x.toFixed(0)}% × {focal.y.toFixed(0)}%</span>
                </p>
                <p className="text-[9px] text-gray-400">Bấm vào ảnh hoặc kéo điểm tròn để chọn vùng quan trọng nhất</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-gray-500">X:</span>
                  <input
                    type="number"
                    value={focal.x}
                    onChange={(e) => {
                      const newFocal = { ...focal, x: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) };
                      setFocal(newFocal);
                      onChange(imageUrl, newFocal);
                    }}
                    className="w-12 border border-gray-200 rounded px-1.5 py-0.5 text-[10px] text-center outline-none"
                    step={1} min={0} max={100}
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-gray-500">Y:</span>
                  <input
                    type="number"
                    value={focal.y}
                    onChange={(e) => {
                      const newFocal = { ...focal, y: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) };
                      setFocal(newFocal);
                      onChange(imageUrl, newFocal);
                    }}
                    className="w-12 border border-gray-200 rounded px-1.5 py-0.5 text-[10px] text-center outline-none"
                    step={1} min={0} max={100}
                  />
                </div>
              </div>
            </div>

            {/* Dual preview: Desktop + Mobile */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] font-semibold text-gray-500 mb-1 flex items-center gap-1">
                  <i className="ri-computer-line" /> {previewLabel} — Desktop (21:9)
                </p>
                <div className="relative rounded-lg overflow-hidden bg-gray-900" style={{ paddingTop: "42.86%" }}>
                  <img
                    src={displayUrl}
                    alt="Desktop preview"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ objectPosition: focalToCssPosition(focal) }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent pointer-events-none" />
                  <span className="absolute bottom-1 left-2 text-white/60 text-[8px] font-bold">DESKTOP</span>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-500 mb-1 flex items-center gap-1">
                  <i className="ri-smartphone-line" /> {previewLabel} — Mobile (4:3)
                </p>
                <div className="relative rounded-lg overflow-hidden bg-gray-900" style={{ paddingTop: "75%" }}>
                  <img
                    src={displayUrl}
                    alt="Mobile preview"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ objectPosition: focalToCssPosition(focal) }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/40 pointer-events-none" />
                  <span className="absolute bottom-1 left-2 text-white/60 text-[8px] font-bold">MOBILE</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Upload zone */
          <div
            className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
              dragOver ? "border-[#004A99] bg-[#EDF2FB]" : "border-gray-200 hover:border-[#004A99]/50 hover:bg-gray-50"
            }`}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onClick={() => inputRef.current?.click()}
          >
            <div className="w-12 h-12 flex items-center justify-center bg-[#EDF2FB] rounded-xl mx-auto mb-3">
              <i className="ri-image-add-line text-2xl text-[#004A99]" />
            </div>
            <p className="text-sm font-semibold text-gray-700 mb-1">
              {uploading ? "Đang xử lý..." : "Kéo thả hoặc bấm để chọn ảnh Banner"}
            </p>
            <p className="text-xs text-gray-400">JPG, PNG, WebP · Khuyến nghị &gt;1920px · Smart Crop & Zoom · Auto WebP</p>
            {uploading && (
              <div className="mt-3 w-32 h-1.5 bg-gray-200 rounded-full overflow-hidden mx-auto">
                <div className="h-full bg-[#004A99] rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
              </div>
            )}
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
    </>
  );
};

export default BannerUploader;
